import React, { useState, useEffect, useRef } from 'react';
import styles from '../../../Style';
import CustomDropdown from '../../../../Components/DropDown/CustomDropdown';
import { Text } from 'react-native';
import { useFormContext } from '../FormProvider';
import { apiCallGetAllExchangeRate } from '../../../../Core/Apicall';

const EventEntry = () => {
  const { formData, setFormData, errors } = useFormContext();
  const [currencies, setCurrencies] = useState([]);
  const [filteredCurrencies, setFilteredCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(1);
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const isInitialMount = useRef(true);
  const isDataLoaded = useRef(false);

  // Fetch exchange rates on component mount
  useEffect(() => {
    fetchExchangeRates();
  }, []);

  // Sync internal state with formData when editing - runs whenever formData changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // If formData has currency and exchange rate, sync them
    if (formData.currency && formData.exchangeRate) {
      setSelectedCurrency(formData.currency);
      setExchangeRate(formData.exchangeRate);
    }

    // If formData has baseCurrency, sync it
    if (formData.baseCurrency) {
      setBaseCurrency(formData.baseCurrency);
    }
  }, [formData.currency, formData.exchangeRate, formData.baseCurrency]);

  // Also sync when currencies are loaded and formData has currency
  useEffect(() => {
    if (currencies.length > 0 && formData.currency && !isDataLoaded.current) {
      const currency = currencies.find(c => c.code === formData.currency);
      if (currency) {
        setSelectedCurrency(formData.currency);
        setExchangeRate(currency.rate);
        isDataLoaded.current = true;
      }
    }
  }, [currencies, formData.currency]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCurrencies(currencies);
    } else {
      const filtered = currencies.filter(currency =>
        currency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        currency.symbol?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        currency.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCurrencies(filtered);
    }
  }, [searchTerm, currencies]);

  // Update formData when exchange rate changes
  useEffect(() => {
    if (exchangeRate && selectedCurrency) {
      setFormData(prevForm => ({
        ...prevForm,
        exchangeRate: exchangeRate,
        currency: selectedCurrency,
        baseCurrency: baseCurrency,
        currencySymbol: getCurrencySymbol(selectedCurrency),
      }));
    }
  }, [exchangeRate, selectedCurrency, baseCurrency]);

  // Recalculate prices when currency/exchange rate changes
  useEffect(() => {
    if (exchangeRate && selectedCurrency && formData.EventEntry === 'Ticket') {
      // Recalculate VIP price - only if displayVipPrice exists
      if (formData.displayVipPrice && formData.displayVipPrice > 0) {
        const usdValue = Number((formData.displayVipPrice / exchangeRate).toFixed(2));
        setFormData(prevForm => ({
          ...prevForm,
          VIPPrice: usdValue,
        }));
      } else if (formData.VIPPrice && !formData.displayVipPrice) {
        // If VIPPrice exists but displayVipPrice doesn't, calculate displayVipPrice
        const displayValue = Number((formData.VIPPrice * exchangeRate).toFixed(2));
        setFormData(prevForm => ({
          ...prevForm,
          displayVipPrice: displayValue
        }));
      }

      // Recalculate Economy price
      if (formData.displayEconomyPrice && formData.displayEconomyPrice > 0) {
        const usdValue = Number((formData.displayEconomyPrice / exchangeRate).toFixed(2));
        setFormData(prevForm => ({
          ...prevForm,
          economyPrice: usdValue,
        }));
      } else if (formData.economyPrice && !formData.displayEconomyPrice) {
        // If economyPrice exists but displayEconomyPrice doesn't, calculate displayEconomyPrice
        const displayValue = Number((formData.economyPrice * exchangeRate).toFixed(2));
        setFormData(prevForm => ({
          ...prevForm,
          displayEconomyPrice: displayValue
        }));
      }
    }
  }, [exchangeRate, selectedCurrency]);

  const fetchExchangeRates = async () => {
    setLoading(true);
    try {
      const response = await apiCallGetAllExchangeRate();
      if (response?.data?.currencies) {
        setCurrencies(response.data.currencies);
        setFilteredCurrencies(response.data.currencies);
        setBaseCurrency(response.data.baseCode);

        // Check if formData has currency (editing mode)
        if (formData.currency) {
          const currency = response.data.currencies.find(c => c.code === formData.currency);
          if (currency) {
            setSelectedCurrency(formData.currency);
            setExchangeRate(currency.rate);
            isDataLoaded.current = true;

            // CRITICAL: Calculate display prices from USD values when editing
            if (formData.EventEntry === 'Ticket') {
              // Calculate display VIP price from USD VIP price
              if (formData.VIPPrice && formData.VIPPrice > 0) {
                const displayVipValue = Number((formData.VIPPrice * currency.rate).toFixed(2));
                setFormData(prev => ({
                  ...prev,
                  displayVipPrice: displayVipValue
                }));
              } else if (formData.displayVipPrice) {
                // If displayVipPrice already exists, keep it but ensure VIPPrice is correct
                const usdValue = Number((formData.displayVipPrice / currency.rate).toFixed(2));
                setFormData(prev => ({
                  ...prev,
                  VIPPrice: usdValue,
                }));
              }

              // Calculate display Economy price from USD Economy price
              if (formData.economyPrice && formData.economyPrice > 0) {
                const displayEconomyValue = Number((formData.economyPrice * currency.rate).toFixed(2));
                setFormData(prev => ({
                  ...prev,
                  displayEconomyPrice: displayEconomyValue
                }));
              } else if (formData.displayEconomyPrice) {
                // If displayEconomyPrice already exists, keep it but ensure economyPrice is correct
                const usdValue = Number((formData.displayEconomyPrice / currency.rate).toFixed(2));
                setFormData(prev => ({
                  ...prev,
                  economyPrice: usdValue,
               
                }));
              }
            }
            return;
          }
        }

        // Set default currency to USD
        const usdCurrency = response.data.currencies.find(c => c.code === 'USD');
        if (usdCurrency) {
          setSelectedCurrency('USD');
          setExchangeRate(usdCurrency.rate);
        } else {
          // Fallback to first currency if USD not found
          setSelectedCurrency(response.data.currencies[0]?.code || 'USD');
          setExchangeRate(response.data.currencies[0]?.rate || 1);
        }
      }
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      // Fallback to default values
      const defaultCurrencies = [
        { code: 'USD', symbol: '$', rate: 1, name: 'US Dollar' },
        { code: 'AED', symbol: 'د.إ', rate: 3.67, name: 'UAE Dirham' },
        { code: 'AFN', symbol: '؋', rate: 71.5, name: 'Afghan Afghani' },
        { code: 'ALL', symbol: 'L', rate: 94.5, name: 'Albanian Lek' },
        { code: 'AMD', symbol: '֏', rate: 387.5, name: 'Armenian Dram' },
      ];
      setCurrencies(defaultCurrencies);
      setFilteredCurrencies(defaultCurrencies);

      // Check if formData has currency (editing mode)
      if (formData.currency) {
        const currency = defaultCurrencies.find(c => c.code === formData.currency);
        if (currency) {
          setSelectedCurrency(formData.currency);
          setExchangeRate(currency.rate);
          isDataLoaded.current = true;

          // Calculate display prices from USD values when editing (fallback)
          if (formData.EventEntry === 'Ticket') {
            if (formData.VIPPrice && formData.VIPPrice > 0) {
              const displayVipValue = Number((formData.VIPPrice * currency.rate).toFixed(2));
              setFormData(prev => ({
                ...prev,
                displayVipPrice: displayVipValue
              }));
            }
            if (formData.economyPrice && formData.economyPrice > 0) {
              const displayEconomyValue = Number((formData.economyPrice * currency.rate).toFixed(2));
              setFormData(prev => ({
                ...prev,
                displayEconomyPrice: displayEconomyValue
              }));
            }
          }
          return;
        }
      }

      setSelectedCurrency('USD');
      setExchangeRate(1);
    } finally {
      setLoading(false);
    }
  };

  const handleCurrencySelect = (currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    if (currency) {
      setSelectedCurrency(currencyCode);
      setExchangeRate(currency.rate);
      setShowCurrencyDropdown(false);
      setSearchTerm('');

      // Update form data with currency info
      setFormData(prevForm => ({
        ...prevForm,
        currency: currencyCode,
        currencySymbol: currency.symbol || currencyCode,
        exchangeRate: currency.rate,
        baseCurrency: baseCurrency,
      }));
    }
  };

  const handleSelectEntry = entry => {
    setFormData(prevForm => ({
      ...prevForm,
      EventEntry: entry,
      paymentProvider: entry === 'Ticket' ? 'Stripe' : 'Free',
      ...(entry === 'Free' && {
        VIPPrice: null,
        VIPSeats: null,
        economyPrice: null,
        economySeats: null,
        ticketPageLink: null,
        displayVipPrice: null,
        displayEconomyPrice: null,

      
      }),
      ...(entry === 'Ticket' && {
        freeSeats: null,
        paymentProvider: 'Stripe'
      })
    }));
  };

  const handleInputChange = (field, value) => {
    // Validate price inputs
    if (field === 'displayVipPrice' || field === 'displayEconomyPrice') {
      if (value && parseFloat(value) < 0) {
        return;
      }
    }

    if (field === 'displayVipPrice') {
      const displayValue = value && value !== '' ? parseFloat(value) : null;
      const usdValue = displayValue && exchangeRate
        ? Number((displayValue / exchangeRate).toFixed(2))
        : null;

      setFormData(prevForm => ({
        ...prevForm,
        displayVipPrice: displayValue,
        VIPPrice: usdValue,
      }));
    } else if (field === 'displayEconomyPrice') {
      const displayValue = value && value !== '' ? parseFloat(value) : null;
      const usdValue = displayValue && exchangeRate
        ? Number((displayValue / exchangeRate).toFixed(2))
        : null;

      setFormData(prevForm => ({
        ...prevForm,
        displayEconomyPrice: displayValue,
        economyPrice: usdValue,
       
      }));
    } else if (field === 'VIPSeats' || field === 'economySeats' || field === 'freeSeats') {
      setFormData(prevForm => ({
        ...prevForm,
        [field]: value && value !== '' ? parseInt(value) : null,
      }));
    } else if (field === 'paymentProvider' || field === 'ticketPageLink') {
      setFormData(prevForm => ({
        ...prevForm,
        [field]: value,
      }));
    } else {
      setFormData(prevForm => ({
        ...prevForm,
        [field]: value,
      }));
    }
  };

  const getCurrencySymbol = (currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    return currency?.symbol || currencyCode;
  };

  const currentCurrencySymbol = getCurrencySymbol(selectedCurrency);

  // Render currency selector with custom UI
  const renderCurrencySelector = () => {
    return (
      <div style={{
        background: '#08285D',
        borderRadius: '12px',
        padding: '15px',
        marginBottom: '20px',
        border: '1px solid #1E5DB8'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          position: 'relative'
        }}>
          <Text style={{ color: '#9FB7E9', fontSize: '14px' }}>Select Currency</Text>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            position: 'relative',
          }}>
            <div style={{
              position: 'relative',
              cursor: 'pointer',
              background: '#0F3A7A',
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #1E5DB8',
              minWidth: '100px',
              textAlign: 'center'
            }}
              onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
            >
              <span style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>
                {currentCurrencySymbol} {selectedCurrency}
              </span>
              <span style={{
                color: '#9FB7E9',
                marginLeft: '8px',
                fontSize: '12px'
              }}>
                ▼
              </span>
            </div>

            {showCurrencyDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                background: '#0F3A7A',
                border: '1px solid #1E5DB8',
                borderRadius: '8px',
                maxHeight: '250px',
                width: '135px',
                zIndex: 1000,
                marginTop: '5px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Search Input */}
                <div style={{
                  padding: '8px 12px',
                  borderBottom: '1px solid rgba(30,93,184,0.3)',
                  position: 'sticky',
                  top: 0,
                  background: '#0F3A7A',
                  zIndex: 1,
                  borderRadius: '8px 8px 0 0'
                }}>
                  <input
                    type="text"
                    placeholder="Search currency..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(30,93,184,0.5)',
                      borderRadius: '6px',
                      color: 'white',
                      fontSize: '13px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setShowCurrencyDropdown(false);
                        setSearchTerm('');
                      }
                    }}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      style={{
                        position: 'absolute',
                        right: '18px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'transparent',
                        border: 'none',
                        color: '#9FB7E9',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Currency List */}
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  padding: '4px 0'
                }}>
                  {loading ? (
                    <div style={{ padding: '20px', color: '#9FB7E9', textAlign: 'center' }}>
                      Loading...
                    </div>
                  ) : filteredCurrencies.length === 0 ? (
                    <div style={{ padding: '20px', color: '#9FB7E9', textAlign: 'center' }}>
                      No currencies found
                    </div>
                  ) : (
                    filteredCurrencies.map(currency => (
                      <div
                        key={currency.code}
                        onClick={() => handleCurrencySelect(currency.code)}
                        style={{
                          padding: '10px 16px',
                          cursor: 'pointer',
                          color: selectedCurrency === currency.code ? 'white' : '#9FB7E9',
                          background: selectedCurrency === currency.code ? 'rgba(99,91,255,0.3)' : 'transparent',
                          borderBottom: '1px solid rgba(30,93,184,0.1)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedCurrency !== currency.code) {
                            e.target.style.background = 'rgba(99,91,255,0.15)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedCurrency !== currency.code) {
                            e.target.style.background = 'transparent';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '600' }}>{currency.code}</span>
                          {currency.name && (
                            <span style={{ color: '#6B8FC4', fontSize: '12px' }}>
                              {currency.name}
                            </span>
                          )}
                        </div>
                        <span style={{ color: '#9FB7E9', fontSize: '12px' }}>
                          {currency.symbol || currency.code}
                        </span>
                      </div>
                    ))
                  )}
                </div>

                {/* Footer with currency count */}
                {!loading && filteredCurrencies.length > 0 && (
                  <div style={{
                    padding: '6px 12px',
                    borderTop: '1px solid rgba(30,93,184,0.3)',
                    color: '#6B8FC4',
                    fontSize: '11px',
                    textAlign: 'center'
                  }}>
                    {filteredCurrencies.length} currency{filteredCurrencies.length > 1 ? 's' : ''} available
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          color: '#6B8FC4',
          fontSize: '12px',
          padding: '5px 5px 0 5px'
        }}>
          <span>1 {baseCurrency} = {exchangeRate.toFixed(4)} {selectedCurrency}</span>
          <span style={{ color: '#4CAF50' }}>
            🟢 You will receive payments in {baseCurrency} (automatically converted)
          </span>
        </div>
      </div>
    );
  };

  return (
    <div>
      <Text style={styles.labelPhoto}>Event Entry</Text>

      <div style={{ margin: '20px 0' }}>
        <div
          className={`Category_name Subcategory_drop ${errors.EventEntry ? 'invalid' : 'valid'}`}
          style={styles.CustomDrop}
        >
          <CustomDropdown
            dropDownLabel={
              formData.EventEntry || (
                <div style={{ color: 'rgb(129, 129, 129)' }}>
                  Select Event Entry
                </div>
              )
            }
            items={['Ticket', 'Free']}
            color='blue'
            onSelect={handleSelectEntry}
            customLabelStyle={{
              fontWeight: '400',
              color: 'white',
              width: '100%'
            }}
          />
        </div>
      </div>

      {formData.EventEntry === 'Ticket' && (
        <div>
          {/* Currency Selector */}
          {renderCurrencySelector()}

     
          {/* Economy Row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '15px',
              marginBottom: '20px'
            }}
          >
            <div className={`Category_name ${errors.displayEconomyPrice ? 'invalid' : 'valid'}`}>
              <input
                type="number"
                step="any"
                placeholder={`Economy Price (${selectedCurrency})`}
                value={formData.displayEconomyPrice !== null && formData.displayEconomyPrice !== undefined ? formData.displayEconomyPrice : ""}
                onChange={e => handleInputChange("displayEconomyPrice", e.target.value)}
                style={{ width: '100%' }}
              />
              {formData.displayEconomyPrice && formData.economyPrice !== null && formData.economyPrice !== undefined && (
                <div style={{
                  color: '#6B8FC4',
                  fontSize: '11px',
                  marginTop: '4px',
                  paddingLeft: '12px'
                }}>
                  USD ${typeof formData.economyPrice === 'number' ? formData.economyPrice.toFixed(2) : '0.00'} (you will receive)
                </div>
              )}
            </div>

            <div className={`Category_name ${errors.EconomySeats ? 'invalid' : 'valid'}`}>
              <input
                type="number"
                placeholder="Economy Seats"
                value={formData.economySeats !== null && formData.economySeats !== undefined ? formData.economySeats : ""}
                onChange={e => handleInputChange("economySeats", e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          </div>

               {/* VIP Row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '15px',
              marginBottom: '20px'
            }}
          >
            <div className={`Category_name ${errors.displayVipPrice ? 'invalid' : 'valid'}`}>
              <input
                type="number"
                step="any"
                placeholder={`VIP Price (${selectedCurrency})`}
                value={formData.displayVipPrice !== null && formData.displayVipPrice !== undefined ? formData.displayVipPrice : ""}
                onChange={e => handleInputChange("displayVipPrice", e.target.value)}
                style={{ width: '100%' }}
              />
              {formData.displayVipPrice && formData.VIPPrice !== null && formData.VIPPrice !== undefined && (
                <div style={{
                  color: '#6B8FC4',
                  fontSize: '11px',
                  marginTop: '4px',
                  paddingLeft: '12px'
                }}>
                  USD ${typeof formData.VIPPrice === 'number' ? formData.VIPPrice.toFixed(2) : '0.00'} (you will receive)
                </div>
              )}
            </div>

            <div className={`Category_name ${errors.VIPSeats ? 'invalid' : 'valid'}`}>
              <input
                type="number"
                placeholder="VIP Seats"
                value={formData.VIPSeats !== null && formData.VIPSeats !== undefined ? formData.VIPSeats : ""}
                onChange={e => handleInputChange("VIPSeats", e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          </div>


          {/* Payment Provider */}
          <div style={{ marginBottom: '20px' }}>
            <Text style={styles.labelPhoto}>Payment Provider</Text>

            <div
              style={{
                display: 'flex',
                width: '320px',
                background: '#08285D',
                border: '1px solid #1E5DB8',
                borderRadius: '14px',
                padding: '4px',
                marginTop: '10px'
              }}
            >
              {['Stripe', 'Third-Party'].map(provider => {
                const isActive = formData.paymentProvider === provider

                return (
                  <button
                    key={provider}
                    type='button'
                    onClick={() =>
                      handleInputChange('paymentProvider', provider)
                    }
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'all 0.25s ease',
                      background: isActive
                        ? 'linear-gradient(135deg,#635BFF,#7C6CFF)'
                        : 'transparent',
                      color: isActive ? '#fff' : '#9FB7E9',
                      boxShadow: isActive
                        ? '0 4px 15px rgba(99,91,255,.35)'
                        : 'none'
                    }}
                  >
                    {isActive && '✓ '}
                    {provider}
                  </button>
                )
              })}
            </div>
          </div>

          {formData.paymentProvider === 'Third-Party' && (
            <div
              className={`Category_name ${errors.ticketPageLink ? 'invalid' : 'valid'}`}
              style={{ marginBottom: '20px' }}
            >
              <input
                type='url'
                placeholder='Ticket Page Link'
                value={formData.ticketPageLink || ''}
                onChange={e =>
                  handleInputChange('ticketPageLink', e.target.value)
                }
                style={{ width: '100%' }}
              />
            </div>
          )}

          {formData.paymentProvider === 'Third-Party' && (
            <div
              style={{
                color: '#9FB7E9',
                fontSize: '12px',
                marginBottom: '10px'
              }}
            >
              Users will be redirected to your external ticket booking page.
            </div>
          )}
        </div>
      )}

      {formData.EventEntry === 'Free' && (
        <div
          className={`Category_name ${errors.freeSeats ? 'invalid' : 'valid'}`}
          style={{ marginBottom: '20px' }}
        >
          <input
            type="number"
            placeholder="Free Seats"
            value={formData.freeSeats !== null && formData.freeSeats !== undefined ? formData.freeSeats : ""}
            onChange={e =>
              handleInputChange('freeSeats', e.target.value)
            }
            style={{ width: '100%' }}
          />
        </div>
      )}
    </div>
  );
};

export default EventEntry;